/** The round brief's items (screens §P4.2): what changed in the market, your last year, what waits in Decide. Shared by the cockpit and Decide's "before you call the year" card. */
import type { ReactNode } from "react";
import type { ContentCatalog, MarketView, RoundReport, TeamState } from "@bsim/contracts";
import { formatBp, formatKpi, formatUsdPerShare, fiscalYearLabel } from "../format";
import type { BriefItem } from "./BriefSheet";
import { stepSummary, stepsToSign } from "../decisions/nextStep";
import { envChange, outlookLine } from "./derive";

export function briefItems(team: TeamState, market: MarketView, catalog: ContentCatalog, score: RoundReport["score"] | null | undefined, rank: string | null): BriefItem[] {
  const env = envChange(market);
  const waiting = stepsToSign(team);
  const outlook = outlookLine(market);
  const items: BriefItem[] = [];
  if (env !== null) {
    // The rates are the market's at the close of the last released year (`throughRoundNo`), the start of the next one, as the Outlook dates them: dated, so no other Fed figure is read against them.
    const startOf = ` at the start of ${fiscalYearLabel(catalog, market.throughRoundNo + 1)}`;
    const rate = (v: number) => <b className="gx-num">{formatKpi("nim", v)}</b>;
    const moved = (label: string, now: number, deltaBp: number | null, held: string, startOf = ""): ReactNode => deltaBp === null ? <>{label} {rate(now)}{startOf}</>
      : deltaBp === 0 ? <>{held} {rate(now)}{startOf}</>
      : <>{label} {rate(now)}{startOf} ({formatBp(deltaBp, { signed: true })})</>;
    items.push({
      key: "changed", title: "What changed",
      body: <><span className="gx-brief-rates"><span>{moved("Policy rate", env.ffRate, env.ffDeltaBp, "Policy rate held at", startOf)}</span><span>{moved("10-year yield", env.tenYearRate, env.tenYearDeltaBp, "10-year yield steady at")}</span></span>{outlook === null ? null : <span className="gx-brief-outlook">{outlook}</span>}</>,
    });
  } else {
    // Before the first year closes there is no market news; the brief points at the consensus, never names the scenario (it would give the answer away).
    items.push({ key: "changed", title: "What changed", body: <>Nothing yet. The Outlook shows the consensus for the year ahead and its range; surprises arrive as cards.</> });
  }
  if (score !== null && score !== undefined) {
    items.push({ key: "yours", title: `Your ${fiscalYearLabel(catalog, team.releasedRounds.at(-1)?.roundNo ?? 0)}`, body: <><b className="gx-num">{Math.abs(score.valueCreatedUsdPerShare) < 0.005 ? "Level with your twin" : `${formatUsdPerShare(score.valueCreatedUsdPerShare, { signed: true })}/share`}</b>{rank === null ? null : ` · ${rank}`}</> });
  }
  items.push({
    key: "waiting", title: "Waiting in Decide",
    body: waiting.length === 0 ? "Nothing waiting" : <><b>{waiting.length} to do:</b> {stepSummary(waiting)}</>,
  });
  return items;
}
